/**
 * Vitrina tepasidagi banner slayderi: bannerlar admin paneldan (`GET /portal/public/banners/`)
 * olinadi, avtomatik almashadi, ustiga kelganda to'xtaydi. Banner bo'lmasa blok ko'rinmaydi.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/shared/api/client";
import type { PortalBanner } from "@/shared/api/types";
import { useI18n } from "@/shared/i18n";

import type { MarketBanner, MarketBannerKind } from "./marketBanners";

const INTERVAL_MS = 5000;

function useBanners(kind: MarketBannerKind): MarketBanner[] {
  const { lang } = useI18n();
  const [list, setList] = useState<MarketBanner[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ results: PortalBanner[] }>(`/portal/public/banners/?kind=${kind}`, { lang })
      .then((data) => {
        if (cancelled) return;
        setList(
          (data.results ?? [])
            .filter((row) => row.image_link)
            .map((row) => ({ id: row.id, image: row.image_link, alt: row.title, to: row.link_url || undefined })),
        );
      })
      .catch(() => !cancelled && setList([]));
    return () => {
      cancelled = true;
    };
  }, [kind, lang]);
  return list;
}

function BannerLink({ to, children, tabIndex }: { to?: string; children: React.ReactNode; tabIndex: number }) {
  if (!to) return <>{children}</>;
  if (/^https?:\/\//i.test(to)) {
    return (
      <a href={to} tabIndex={tabIndex} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link to={to} tabIndex={tabIndex}>
      {children}
    </Link>
  );
}

export function MarketBanners({ kind }: { kind: MarketBannerKind }) {
  const list = useBanners(kind);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (list.length < 2 || paused) return;
    const timer = window.setInterval(() => setIndex((value) => (value + 1) % list.length), INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [list.length, paused]);

  useEffect(() => {
    if (index >= list.length) setIndex(0);
  }, [list.length, index]);

  if (!list.length) return null;
  const go = (step: number) => setIndex((value) => (value + step + list.length) % list.length);
  return (
    <section
      className="market-banners"
      aria-roledescription="carousel"
      aria-label="Aksiyalar va e'lonlar"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="market-banners-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {list.map((banner, i) => (
          <div className="market-banner" key={banner.id} aria-hidden={i !== index}>
            <BannerLink to={banner.to} tabIndex={i === index ? 0 : -1}>
              <img src={banner.image} alt={banner.alt} loading={i === 0 ? "eager" : "lazy"} draggable={false} />
            </BannerLink>
          </div>
        ))}
      </div>
      {list.length > 1 ? (
        <>
          <button type="button" className="market-banners-nav is-prev" aria-label="Oldingi banner" onClick={() => go(-1)}>
            ‹
          </button>
          <button type="button" className="market-banners-nav is-next" aria-label="Keyingi banner" onClick={() => go(1)}>
            ›
          </button>
          <div className="market-banners-dots">
            {list.map((banner, i) => (
              <button
                key={banner.id}
                type="button"
                className={i === index ? "is-on" : ""}
                aria-label={`Banner ${i + 1}`}
                aria-current={i === index}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
